import clsx from 'clsx';
import { type ReactElement, useEffect, useMemo, useRef } from 'react';

import CardImage from '@/components/CardImage/CardImage';
import useCardLookup from '@/hooks/useCardLookup';
import { getDisplayImageUris } from '@/lib/cardUtils';
import type { MinimalCard } from '@/services/cardDatabase';

interface PackCardProps {
  card: MinimalCard;
  isSelected: boolean;
  isFocused: boolean;
  isRecommended: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
}

export default function PackCard({ card, isSelected, isFocused, isRecommended, onClick, onDoubleClick }: PackCardProps): ReactElement {
  const ref = useRef<HTMLButtonElement>(null);
  const { getFullCard } = useCardLookup();

  // Prefer full card images (resolves DFCs correctly)
  const imageUris = useMemo(() => {
    const fullCard = getFullCard(card.id);
    if (fullCard) return getDisplayImageUris(fullCard);
    return card.image_uris;
  }, [card.id, card.image_uris, getFullCard]);

  useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isFocused]);

  return (
    <button
      ref={ref}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      aria-label={`${card.name} - ${card.rarity}`}
      className={clsx(
        'group relative w-fit cursor-pointer rounded-lg transition-all duration-150',
        isSelected && 'scale-[1.02] ring-2 ring-amber-400 ring-offset-1 ring-offset-zinc-900',
        !isSelected && isRecommended && 'ring-2 ring-green-400/70 ring-offset-1 ring-offset-zinc-900',
        !isSelected && !isRecommended && 'hover:scale-[1.03] hover:ring-1 hover:ring-zinc-500',
      )}
    >
      <CardImage imageUris={imageUris} name={card.name} size="normal" className="rounded-lg" />

      {/* Selected checkmark */}
      {isSelected && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-amber-500/10">
          <div className="rounded-full bg-amber-400 p-1">
            <svg className="h-4 w-4 text-zinc-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      )}
    </button>
  );
}
